import React, { FunctionComponent, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent, Typography, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { toast } from "react-toastify";

import { useStore } from "../../helpers";

import ProfileQuestionOptions from "./ProfileQuestionOptions";
import CreateANDEditOptionsDialog from "../components/CreateANDEditOptionsDialog";
import { formatName } from "../utitlity/formatName";
import { TailwindModal } from "../../components/TailwindModal";
import { FTPPagination } from "../../components-new/FTPPagination";
import { SideOverlay } from "../../components/side-overlay";
import { FTPSearchBar } from "../../components-new/FTPSearchBar";

type CasteBankPayload = {
    limit: number;
    offset: number;
    name?: string;
};

const PAGE_SIZE = 50;

const OptionsTab: FunctionComponent<any> = observer(({ handleSave }) => {
    const { questionStore, projectStore } = useStore();

    const current_data = questionStore.activeTab;
    const questiontitle = formatName(current_data.title);

    const [showAddModal, setShowAddModal] = useState(false);
    const [showImportOverlay, setShowImportOverlay] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);

    const [searchQuery, setSearchQuery] = useState("");
    const [pageNumber, setPageNumber] = useState(1);
    const [resultsSize, setResultsSize] = useState(0);
    const [casteBankName, setCasteBankName] = useState("");

    const fetchCasteBankListMutation = useMutation<
        any,
        unknown,
        CasteBankPayload
    >({
        mutationFn: (payload) => projectStore.import_caste(payload),

        onSuccess: (res) => {
            // questionStore.setCasteBankList(res?.data?.results ?? []);
            questionStore.setCasteBankList(res?.data?.results?.[0] ?? []);
            // setResultsSize(res?.data?.count ?? 0);
            setResultsSize(res?.data?.total ?? 0);
        },

        onError: () => {
            toast.error("Failed to fetch caste banks");
        },
    });

    useEffect(() => {
        if (!showImportOverlay) return;

        fetchCasteBankListMutation.mutate({
            limit: PAGE_SIZE,
            offset: (pageNumber - 1) * PAGE_SIZE,
            name: searchQuery,
        });
    }, [pageNumber, searchQuery, showImportOverlay]);

    const handleImportClick = () => {
        setShowImportOverlay(true);
        setSearchQuery("");
        setPageNumber(1);
    };

    const handleSearch = (value: string) => {
        setSearchQuery(value);
        setPageNumber(1);
    };

    const handlePageChange = (_: any, page: number) => {
        setPageNumber(page);
    };

    const handleImportFromCard = (bank: any) => {
        const incoming = Array.isArray(bank.data)
            ? bank.data
            : bank.data
              ? [bank.data]
              : [];

        const existing = current_data.data ?? [];

        const merged = [
            ...existing,
            ...incoming.filter(
                (n) => !existing.some((e) => e.value === n.value)
            ),
        ];

        if (merged.length === existing.length) {
            toast.info("No new options found to import");
            return;
        }

        questionStore.updateOption(current_data.title, merged);
        toast.success(
            `Imported ${merged.length - existing.length} new options`
        );
        setShowImportOverlay(false);
    };

    const handleDeleteCasteBank = async (bankId: number) => {
        if (
            !window.confirm("Are you sure you want to delete this caste bank?")
        ) {
            return;
        }

        try {
            const response = await projectStore.delete_caste_bank(bankId);
            if (response?.status === 200 || response?.status === 204) {
                toast.success("Caste bank deleted successfully");
                // Refresh the list
                fetchCasteBankListMutation.mutate({
                    limit: PAGE_SIZE,
                    offset: (pageNumber - 1) * PAGE_SIZE,
                    name: searchQuery,
                });
            } else {
                toast.error("Failed to delete caste bank");
            }
        } catch {
            toast.error("Server error while deleting caste bank");
        }
    };

    const handleExportSubmit = async () => {
        if (!casteBankName.trim()) {
            toast.error("Please enter caste bank name");
            return;
        }

        try {
            const response = await projectStore.export_caste({
                name: casteBankName,
                ds: current_data.data,
            });

            if (response?.status === 200 || response?.status === 201) {
                toast.success("Caste bank exported successfully");
                setShowExportModal(false);
                setCasteBankName("");
            } else if (response?.status === 422) {
                toast.error("Caste bank with this name already exists");
            } else {
                toast.error("Failed to export caste bank");
            }
        } catch {
            toast.error("Server error while exporting caste bank");
        }
    };

    const totalPages = Math.ceil(resultsSize / PAGE_SIZE);

    return (
        <>
            <CreateANDEditOptionsDialog
                show={showAddModal}
                type="Add"
                title={questiontitle}
                currentValue=""
                id=""
                onSubmit={(options) =>
                    questionStore.addOption(current_data.title, options)
                }
                setShow={setShowAddModal}
            />

            <Card className="h-full w-full">
                <CardContent>
                    <div className="flex w-full items-center justify-between">
                        <Typography variant="h5">List of Options</Typography>

                        <div className="flex gap-2">
                            {questiontitle === "Caste" && (
                                <>
                                    <Button
                                        variant="contained"
                                        onClick={handleImportClick}
                                    >
                                        Import
                                    </Button>

                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={() => setShowExportModal(true)}
                                    >
                                        Export
                                    </Button>
                                </>
                            )}

                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<AddIcon />}
                                onClick={() => setShowAddModal(true)}
                            >
                                {questiontitle}
                            </Button>

                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleSave}
                            >
                                Save
                            </Button>
                        </div>
                    </div>

                    <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
                        {current_data.data.map(({ label }, id) => (
                            <ProfileQuestionOptions
                                key={id}
                                data={label}
                                id={id}
                                title={current_data.title}
                                onUpdate={(val) =>
                                    questionStore.updateOption(
                                        current_data.title,
                                        current_data.data.map((d, i) =>
                                            i === id
                                                ? { label: val, value: val }
                                                : d
                                        )
                                    )
                                }
                                onDelete={(opt) =>
                                    questionStore.deleteOption(
                                        current_data.title,
                                        opt
                                    )
                                }
                            />
                        ))}
                    </ul>
                </CardContent>
            </Card>

            <SideOverlay
                show={showImportOverlay}
                onClose={() => setShowImportOverlay(false)}
                title="Import from Caste Bank"
                size="large"
            >
                <div className="flex h-full flex-col">
                    {/* SEARCH */}
                    <div className="mb-4">
                        <FTPSearchBar
                            placeHolder="Search caste bank..."
                            onSearch={handleSearch}
                        />
                    </div>

                    {/* LIST */}
                    <div className="min-h-0 flex-1 overflow-y-auto">
                        <div className="space-y-2">
                            {questionStore.casteBankList.map((bank: any) => {
                                const casteCount = Array.isArray(bank.data)
                                    ? bank.data.length
                                    : bank.data
                                      ? 1
                                      : 0;
                                return (
                                    <div
                                        key={bank.id}
                                        className="group flex items-center justify-between rounded-md border border-gray-200 bg-white p-3 shadow-sm transition-all hover:border-indigo-300 hover:shadow-md"
                                    >
                                        <div className="min-w-0 flex-1 pr-3">
                                            <h3 className="mb-0.5 truncate text-sm font-semibold text-gray-900">
                                                {bank.name}
                                            </h3>
                                            <p className="text-xs text-gray-500">
                                                {casteCount}{" "}
                                                {casteCount === 1
                                                    ? "Caste"
                                                    : "Castes"}
                                            </p>
                                        </div>

                                        <div className="flex flex-shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                                            <button
                                                className="rounded p-1.5 text-indigo-600 transition-colors hover:bg-indigo-50"
                                                onClick={() =>
                                                    handleImportFromCard(bank)
                                                }
                                                title="Import"
                                            >
                                                <FileDownloadIcon fontSize="small" />
                                            </button>
                                            <button
                                                className="rounded p-1.5 text-red-600 transition-colors hover:bg-red-50"
                                                onClick={() =>
                                                    handleDeleteCasteBank(
                                                        bank.id
                                                    )
                                                }
                                                title="Delete"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* PAGINATION */}
                    {resultsSize > PAGE_SIZE && (
                        <div className="mt-4 flex flex-col items-center justify-between gap-3 border-t border-gray-200 pt-4 sm:flex-row">
                            <span className="text-xs text-gray-500">
                                Showing {(pageNumber - 1) * PAGE_SIZE + 1}–
                                {Math.min(pageNumber * PAGE_SIZE, resultsSize)}{" "}
                                of {resultsSize} results
                            </span>

                            <FTPPagination
                                totalPages={totalPages}
                                pageNumber={pageNumber}
                                handlePageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </SideOverlay>

            <TailwindModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
            >
                <div className="w-[90vw] max-w-md rounded-xl bg-white p-6">
                    <h2 className="mb-4 text-xl font-semibold">
                        Export Caste Bank
                    </h2>

                    <input
                        type="text"
                        value={casteBankName}
                        maxLength={40}
                        onChange={(e) => setCasteBankName(e.target.value)}
                        placeholder="Caste Bank Name"
                        className="mb-4 w-full rounded-md border px-3 py-2"
                    />

                    <div className="flex justify-end gap-3">
                        <button
                            onClick={() => setShowExportModal(false)}
                            className="text-gray-600"
                        >
                            Cancel
                        </button>

                        <button
                            className="rounded-md bg-indigo-600 px-4 py-2 text-white"
                            onClick={handleExportSubmit}
                        >
                            Export
                        </button>
                    </div>
                </div>
            </TailwindModal>
        </>
    );
});

export default OptionsTab;
